import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {RootState} from "../../../../../../app/store";
import {
    BotIntegrations,
    DeleteIntegrationRequest,
    Integration,
    ToggleIntegrationRequest
} from "../../../../../../api/generated";
import {integrationApi, RequestStatus} from "../../../../../../api/apiConfiguration";
import {connectIntegration} from "./connectIntegrationSlice";
import {updateIntegration} from "./updateIntegrationSlice";

export type BotIntegrationData = {
    integration: Integration
    data: any
}

interface BotState {
    botIntegrationDataList: BotIntegrationData[]
    botIntegrationsRequestStatus: RequestStatus
}

const initialState: BotState = {
    botIntegrationDataList: [] as BotIntegrationData[],
    botIntegrationsRequestStatus: RequestStatus.NONE
}

export const fetchBotIntegrations = createAsyncThunk(
    'integrations/fetchBotIntegrations',
    (publicId: string) => integrationApi.getBotIntegrations({publicId})
)

export const deleteIntegration = createAsyncThunk<BotIntegrations, DeleteIntegrationRequest>(
    'integrations/deleteIntegration',
    (request) => {
        return integrationApi.deleteIntegration({deleteIntegrationRequest: request})
    }
)

export const toggleIntegration = createAsyncThunk<BotIntegrations, ToggleIntegrationRequest>(
    'integrations/toggleIntegration',
    (request) => {
        return integrationApi.toggleIntegration({toggleIntegrationRequest: request})
    }
)

export const integrationsSlice = createSlice({
    name: 'integrations',
    initialState,
    reducers: {},
    extraReducers: builder => {
        builder.addCase(connectIntegration.fulfilled, (state, {payload}) => {
            state.botIntegrationDataList = mapToBotIntegrationDataList(payload);
        });
        builder.addCase(deleteIntegration.fulfilled, (state, {payload}) => {
            state.botIntegrationDataList = mapToBotIntegrationDataList(payload);
        });
        builder.addCase(toggleIntegration.fulfilled, (state, {payload}) => {
            state.botIntegrationDataList = mapToBotIntegrationDataList(payload);
        });
        builder.addCase(fetchBotIntegrations.fulfilled, (state, {payload}) => {
            state.botIntegrationDataList = mapToBotIntegrationDataList(payload);
            state.botIntegrationsRequestStatus = RequestStatus.RESOLVED
        });
        builder.addCase(fetchBotIntegrations.pending, (state) => {
            state.botIntegrationsRequestStatus = RequestStatus.PENDING
        });
        builder.addCase(fetchBotIntegrations.rejected, (state) => {
            state.botIntegrationsRequestStatus = RequestStatus.REJECTED
        });
        builder.addCase(updateIntegration.fulfilled, (state, {payload}) => {
            state.botIntegrationDataList = mapToBotIntegrationDataList(payload);
        });
    }
})

export const ALL_INTEGRATIONS = [
    Integration.Superchat,
]

export const ALL_INTEGRATIONS_DATA = (botIntegrations: BotIntegrations) => [
    {
        integration: Integration.Superchat,
        data: botIntegrations.superchatConfiguration!
    }
] as BotIntegrationData[]

const mapToBotIntegrationDataList = (botIntegrations: BotIntegrations): BotIntegrationData[] => {
    return ALL_INTEGRATIONS_DATA(botIntegrations).filter(i => !!i.data)
}

export const _ = integrationsSlice.actions;

export const botIntegrationDataListSelector = (state: RootState): BotIntegrationData[] => state.integrations.botIntegrationDataList;
export const botIntegrationsRequestStatusSelector = (state: RootState): RequestStatus => state.integrations.botIntegrationsRequestStatus;
